//  TabLock
//  Enforces a single active ED3N tab per browser profile.
//
//  Why this exists: all auth state lives in per-tab sessionStorage, but SAMS refresh
//  tokens are single-use. When a user duplicates a tab (Ctrl+click, "Duplicate tab",
//  restored session, etc.) the copy inherits the same refresh token. The two tabs then
//  race to rotate it, one loses, and that tab is handed a 401 mid-work. TabLock stops a
//  second tab from ever reaching the authenticated app so the race cannot happen.
//
//  Mechanism: ownership is advertised through a shared localStorage record carrying the
//  owning tab's id and a periodically refreshed timestamp (a heartbeat). A tab is the
//  owner if the record names it; the record is considered stale (owner gone) once the
//  heartbeat is older than TTL_MS. Claiming writes the record and then reads it back to
//  resolve simultaneous cold-start races.
//
//  Fail-open by design: every storage access is guarded. If localStorage is unavailable
//  or throws (private mode, quota, disabled), TabLock reports "no other tab / I am owner"
//  so it can never lock a legitimate sole user out of the app.

//  Unique per tab. Regenerated on every full document load, so a duplicated tab gets its
//  own id and is correctly recognised as a different tab from the original.
const TAB_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : String(Date.now()) + '-' + Math.random().toString(16).slice(2);

//  Storage key is intentionally kept private to this module (not in SessionConstants) so
//  TabLock stays entirely self-contained and cannot collide with unrelated session keys.
const KEY = 'ed3n:activeTab';

//  A record older than this is treated as an abandoned owner (crash / hard close). Must be
//  comfortably larger than HEARTBEAT_MS so a live owner is never mistaken for gone.
const TTL_MS = 10000;

//  How often the owner refreshes its timestamp while the tab is alive.
const HEARTBEAT_MS = 4000;

let heartbeatTimer = null;

//  Listeners notified when this tab discovers it has lost ownership (a different tab took
//  over while this one was backgrounded/asleep and its heartbeat lapsed past the TTL).
//  Without this, a tab that ran claimOwnership() once at mount would keep running as an
//  authenticated "zombie" alongside the new owner, and both would race the single-use
//  refresh token - exactly what TabLock exists to prevent.
const ownershipLostListeners = new Set();

//  subscribeOwnershipLost(listener)
//  Registers a callback fired once when this tab loses ownership to a live sibling. Returns
//  an unsubscribe function.
function subscribeOwnershipLost(listener) {
    ownershipLostListeners.add(listener);
    return () => ownershipLostListeners.delete(listener);
}

//  notifyOwnershipLost()
//  Stops competing for the lock and tells subscribers ownership is gone. Guarded so a
//  throwing listener cannot break the others.
function notifyOwnershipLost() {
    stopHeartbeat();
    ownershipLostListeners.forEach((listener) => {
        try {
            listener();
        } catch {
            //  A subscriber threw; keep notifying the rest.
        }
    });
}

function readRecord() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const record = JSON.parse(raw);
        if (!record || typeof record.id !== 'string' || typeof record.ts !== 'number') {
            return null;
        }
        return record;
    } catch {
        //  Storage unavailable or corrupt value: behave as if no owner is recorded.
        return null;
    }
}

function writeRecord() {
    try {
        localStorage.setItem(KEY, JSON.stringify({ id: TAB_ID, ts: Date.now() }));
        return true;
    } catch {
        return false;
    }
}

function isFresh(record) {
    return Boolean(record) && (Date.now() - record.ts) < TTL_MS;
}

//  anotherTabActive()
//  True only when a *different* tab currently holds a fresh claim. Used as a gate before
//  starting a login so a second tab is stopped before it authenticates.
function anotherTabActive() {
    const record = readRecord();
    if (!isFresh(record)) return false;      //  no owner, or owner is stale/gone
    return record.id !== TAB_ID;             //  a live owner that is not us
}

//  isOwner()
//  True when the current tab is the recorded, fresh owner.
function isOwner() {
    const record = readRecord();
    return isFresh(record) && record.id === TAB_ID;
}

//  claimOwnership()
//  Attempts to become the single active tab. Returns true on success, false if another
//  live tab already owns the app. Fail-open: if storage is unavailable, returns true.
//
//  Handles the simultaneous cold-start race (two fresh tabs claiming at once) by writing
//  the record and reading it straight back; if the read-back no longer names us, the other
//  tab won and we back off.
function claimOwnership() {
    const record = readRecord();

    //  Someone else holds a fresh claim: do not take it.
    if (isFresh(record) && record.id !== TAB_ID) {
        return false;
    }

    //  Vacant, stale, or already ours: claim it.
    if (!writeRecord()) {
        //  Storage write failed entirely: fail open so a sole user is never blocked.
        startHeartbeat();
        return true;
    }

    //  Read-back to resolve two tabs claiming in the same instant. Last writer wins; if
    //  that is not us, yield.
    const confirmed = readRecord();
    if (confirmed && confirmed.id !== TAB_ID) {
        return false;
    }

    startHeartbeat();
    return true;
}

//  releaseOwnership()
//  Relinquishes the claim if (and only if) we currently hold it, so a sibling tab can take
//  over immediately instead of waiting out the TTL. Safe to call unconditionally.
function releaseOwnership() {
    stopHeartbeat();
    try {
        const record = readRecord();
        if (record && record.id === TAB_ID) {
            localStorage.removeItem(KEY);
        }
    } catch {
        //  Nothing more we can do; the TTL will expire the claim.
    }
}

function startHeartbeat() {
    if (heartbeatTimer !== null) return;
    heartbeatTimer = setInterval(() => {
        //  Read the record once so the decision is based on a single consistent snapshot
        //  rather than two separate reads.
        const record = readRecord();
        if (isFresh(record) && record.id !== TAB_ID) {
            //  A different tab holds a fresh claim: it took over while this tab was
            //  backgrounded/asleep and its heartbeat lapsed past the TTL. Do not stomp the
            //  live owner - instead surrender, stop the heartbeat, and notify subscribers so
            //  this now-owner-less tab can drop out of the authenticated app.
            notifyOwnershipLost();
            return;
        }
        //  Still ours, or vacant/stale: (re)assert the claim, then read it straight back.
        //  localStorage offers no atomic compare-and-swap, so a sibling tab could have
        //  claimed in the tiny gap between the read above and this write; the read-back
        //  mirrors claimOwnership's arbitration so if a foreign tab's write landed last we
        //  detect it here and surrender immediately instead of both tabs believing they own
        //  the lock. Any residual same-instant overlap is still self-healing: whichever tab
        //  does not hold the record surrenders on its next heartbeat, so the system converges
        //  to a single owner within one HEARTBEAT_MS. A fully atomic guarantee would require
        //  the Web Locks API (navigator.locks); this best-effort protocol is intentionally
        //  fail-open so storage quirks can never lock out a legitimate sole user.
        if (!writeRecord()) return;
        const confirmed = readRecord();
        if (confirmed && confirmed.id !== TAB_ID) {
            notifyOwnershipLost();
        }
    }, HEARTBEAT_MS);
}

function stopHeartbeat() {
    if (heartbeatTimer !== null) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

//  Release the claim when the tab goes away so the next tab can start without waiting for
//  the TTL. pagehide covers close / navigation / bfcache; the visibilitychange handler
//  re-asserts ownership when a still-authorized owner tab is brought back to the front.
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', releaseOwnership);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && isOwner()) {
            writeRecord();
        }
    });
}

const TabLock = {
    TAB_ID,
    anotherTabActive,
    isOwner,
    claimOwnership,
    releaseOwnership,
    subscribeOwnershipLost,
};

export default TabLock;
